import React from 'react';
import { withTranslation } from 'react-i18next';
import PageHeader from './boxes/PageHeader';
import PageBody from './boxes/PageBody';
import MainContent from './boxes/MainContent';
import LoadingScreen from './LoadingScreen';
import { appConfig } from '../config/appConfig';
import clientService from '../services/client';

const buildIframeSrc = ({
    proxyBaseUrl,
    token,
    clientName,
    clientCode,
    clientEmail,
    monolithUrl,
    country,
    submitterEmail,
    adminEmails = []
}) => {
    if (
        !proxyBaseUrl ||
        !token ||
        !clientName ||
        !clientCode ||
        !clientEmail ||
        !monolithUrl ||
        !country
    ) {
        return null;
    }
    const trimmedBase = proxyBaseUrl.replace(/\/+$/, '');
    const encodedToken = encodeURIComponent(token);
    const encodedName = encodeURIComponent(clientName);
    const encodedCode = encodeURIComponent(clientCode);
    const encodedEmail = encodeURIComponent(clientEmail);
    const encodedMonolith = encodeURIComponent(monolithUrl);
    const encodedCountry = encodeURIComponent(country);
    const submitterEmailSegment = submitterEmail
        ? `&c_submitter_email=${encodeURIComponent(submitterEmail)}`
        : '';
    const adminEmailsSegment =
        adminEmails && adminEmails.length > 0
            ? `&c_adcontact=${encodeURIComponent(
                  adminEmails.map((email) => `${email} <br /> <br />`).join('')
              )}`
            : '';
    return `${trimmedBase}/zoho-form?c_ref=${encodedToken}&c_name=${encodedName}&c_code=${encodedCode}&c_email=${encodedEmail}&c_monolith=${encodedMonolith}&c_country=${encodedCountry}${submitterEmailSegment}${adminEmailsSegment}`;
};

// If iframe takes longer than this to fire `load`, treat as failed. Catches CSP frame-ancestors / X-Frame-Options,
// refusals, DNS/network failures, and proxy hangs, none of which fire `onError` for cross-origin frames.
const IFRAME_LOAD_TIMEOUT_MS = 10000;

class RequestsComponent extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, iframeKey: 0, adminEmails: [], adminsLoaded: false };
        this.loadWatchTimer = null;
    }

    componentDidMount() {
        this.fetchAdminEmails();
    }

    fetchAdminEmails = () => {
        const { user } = this.props;
        const accessToken = user?.credentials?.accessToken;
        const clientId = user?.preferences?.selectedClient;

        if (!accessToken || !clientId || clientId === -1) {
            this.setState({ adminsLoaded: true }, this.onAdminsLoaded);
            return;
        }

        clientService
            .getClientAdmins(accessToken, clientId)
            .then((result) => {
                this.setState(
                    {
                        adminEmails: Array.isArray(result) ? result : [],
                        adminsLoaded: true
                    },
                    this.onAdminsLoaded
                );
            })
            .catch((err) => {
                console.error('Failed to load admin emails for Requests form', err);
                this.setState(
                    { adminEmails: [], adminsLoaded: true },
                    this.onAdminsLoaded
                );
            });
    };

    onAdminsLoaded = () => {
        if (this.buildSrc()) this.startLoadWatchTimer();
    };

    componentWillUnmount() {
        this.clearLoadWatchTimer();
    }

    buildSrc = () => {
        const { user } = this.props;
        return buildIframeSrc({
            proxyBaseUrl: appConfig.requestsProxyBaseUrl,
            token: user?.credentials?.accessToken,
            clientName: user?.companyInfo?.name,
            clientCode: user?.preferences?.clientShortCode,
            clientEmail: user?.companyInfo?.email,
            monolithUrl: appConfig.monolithBaseUrl,
            country: appConfig.country,
            submitterEmail: user?.adminInfo?.email,
            adminEmails: this.state.adminEmails
        });
    };

    startLoadWatchTimer = () => {
        this.clearLoadWatchTimer();
        this.loadWatchTimer = setTimeout(() => {
            this.loadWatchTimer = null;
            this.setState({ hasError: true });
        }, IFRAME_LOAD_TIMEOUT_MS);
    };

    clearLoadWatchTimer = () => {
        if (this.loadWatchTimer) {
            clearTimeout(this.loadWatchTimer);
            this.loadWatchTimer = null;
        }
    };

    handleIframeLoad = () => {
        this.clearLoadWatchTimer();
    };

    handleIframeError = () => {
        this.clearLoadWatchTimer();
        this.setState({ hasError: true });
    };

    handleRetry = () => {
        if (!this.buildSrc()) return;
        this.setState(
            (prev) => ({ hasError: false, iframeKey: prev.iframeKey + 1 }),
            this.startLoadWatchTimer
        );
    };

    renderError() {
        const { t } = this.props;
        return (
            <div
                style={{
                    width: '100%',
                    height: '60vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '20px'
                }}
            >
                <p style={{ marginBottom: '16px' }}>{t('requests.errorMessage')}</p>
                {this.buildSrc() && (
                    <button onClick={this.handleRetry} style={{ cursor: 'pointer' }}>
                        {t('requests.retry')}
                    </button>
                )}
            </div>
        );
    }

    render() {
        const { t } = this.props;

        if (!this.state.adminsLoaded) {
            return (
                <MainContent>
                    <PageHeader title={t('requests.pageTitle')} />
                    <PageBody>
                        <div style={{ width: '100%', height: '60vh', position: 'relative' }}>
                            <LoadingScreen />
                        </div>
                    </PageBody>
                </MainContent>
            );
        }

        const iframeSrc = this.buildSrc();
        const showError = this.state.hasError || !iframeSrc;

        return (
            <MainContent>
                <PageHeader title={t('requests.pageTitle')} />
                <PageBody>
                    {showError ? (
                        this.renderError()
                    ) : (
                        <div style={{ width: '100%', height: '80vh', display: 'flex' }}>
                            <iframe
                                key={this.state.iframeKey}
                                title={t('requests.pageTitle')}
                                src={iframeSrc}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', flex: 1 }}
                                allowtransparency="true"
                                // onLoad plus watchTimer (componentDidMount) is the primary detector, onError doesn't fire for
                                // cross-origin HTTP errors, CSP frame-ancestors blocks, or X-Frame-Options denials.
                                onLoad={this.handleIframeLoad}
                                onError={this.handleIframeError}
                            />
                        </div>
                    )}
                </PageBody>
            </MainContent>
        );
    }
}

export default withTranslation()(RequestsComponent);
